import { Divider, Stack, Typography } from "@mui/material";
import { ReuseModal } from "components/ui/ReuseModal";
import React, { useEffect, useState } from "react";
import { EngagementSettingsItem } from "./EngagementSettingsItem";
import {
  EngagementScoreConfig,
  EngagementScoreValues,
} from "../../../../types/apiTypes";
import { QuestionMarkInCircleIcon } from "../../../images/QuestionMarkInCircleIcon";
import { ReuseTooltip } from "../../ReuseTooltip";
import { FieldProgress } from "./FieldProgress";
import { EngagementSettingsDays } from "./EngagementScoreSettingDays";
import { CustomCheckbox } from "../../CustomCheckbox";

type Fields = { [key: string]: EngagementScoreValues };

type Props = {
  isOpen: boolean;
  isLoading: boolean;
  setIsOpen: (isOpen: boolean) => void;
  settings: EngagementScoreConfig;
  onSubmit: (value: EngagementScoreConfig) => void;
};

export const EngagementScoreSettingModal = ({
  isOpen,
  setIsOpen,
  settings,
  onSubmit,
  isLoading,
}: Props) => {
  const { recency_days, all_campaigns, ...rest } = settings;
  const [isAllProducts, setIsAllProducts] = useState<boolean>(all_campaigns);
  const [days, setDays] = useState<number>(recency_days);
  const [formData, setFormData] = useState<Fields>(rest);

  useEffect(() => {
    if (settings) {
      setFormData(rest);
      setDays(recency_days);
      setIsAllProducts(all_campaigns);
    }
  }, [settings]);

  const onChangeHandler = (field: string, value: EngagementScoreValues) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const percentage = Object.values(formData).reduce(
    (acc, { weight, enabled }) => {
      if (enabled) {
        return acc + weight;
      }
      return acc;
    },
    0,
  );

  const newData = {
    calls_made: formData.calls_made,
    calls_answered: formData.calls_answered,
    emails_sent: formData.emails_sent,
    emails_answered: formData.emails_answered,
    meetings: formData.meetings,
    recency: formData.recency,
    recency_days: days,
    all_campaigns: isAllProducts,
  };

  const isNoChanges = JSON.stringify(newData) === JSON.stringify(settings);

  return (
    <ReuseModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title=""
      maxWidth="548px"
      submitBtnText="Apply"
      submitBtnColor="#1C3C6C"
      onSubmit={() => onSubmit(newData)}
      isLoading={isLoading}
      isDisabled={percentage !== 100 || isLoading || isNoChanges}
    >
      <Stack gap="8px">
        <Stack gap="16px" alignItems="flex-start" paddingBottom="16px">
          <Typography variant="h4" color="#1F2932" fontWeight={500}>
            Engagement Score Settings
          </Typography>
          <Typography
            variant="subtitle1"
            color="#4C545B"
            textAlign="left"
            fontWeight={400}
          >
            <span style={{ fontWeight: 500, color: "#1F2932" }}>
              Adjust the impact
            </span>{" "}
            of each metric on the engagement score calculation and set how long{" "}
            <span style={{ fontWeight: 500, color: "#1F2932" }}>
              activity is considered "recent.
            </span>
            "
          </Typography>
        </Stack>
        <Stack
          sx={{
            padding: "24px 16px",
            border: "1px solid #D9D9D9",
            borderRadius: "8px",
            gap: "16px",
          }}
        >
          <Stack gap="4px">
            <Typography variant="button" color="#1F2932">
              Calls Impact
            </Typography>

            {["calls_made", "calls_answered"].map((field) => (
              <EngagementSettingsItem
                key={field}
                item={formData[field]}
                field={field}
                onChange={onChangeHandler}
              />
            ))}
          </Stack>
          <Stack gap="4px">
            <Typography variant="button" color="#1F2932">
              Emails Impact
            </Typography>

            {["emails_sent", "emails_answered"].map((field) => (
              <EngagementSettingsItem
                key={field}
                item={formData[field]}
                field={field}
                onChange={onChangeHandler}
              />
            ))}
          </Stack>
          <Stack gap="4px">
            <Typography variant="button" color="#1F2932">
              Meetings Impact
            </Typography>

            {["meetings"].map((field) => (
              <EngagementSettingsItem
                key={field}
                item={formData[field]}
                field={field}
                onChange={onChangeHandler}
              />
            ))}
          </Stack>
          <Stack gap="4px" justifyContent="center">
            <Stack flexDirection="row" gap="4px">
              <Typography variant="button" color="#1F2932">
                Recency Impact
              </Typography>
              <ReuseTooltip
                text="Communication is evaluated over the entire available period, with recent interactions
               having the most impact. New interactions count fully, gradually decreasing in value over time until
                they reach a minimum at the end of the recency period (90 days by default). This period can be
                 customized to fit your needs."
              >
                <QuestionMarkInCircleIcon />
              </ReuseTooltip>
            </Stack>

            {["recency"].map((field) => (
              <EngagementSettingsItem
                key={field}
                item={formData[field]}
                field={field}
                onChange={onChangeHandler}
              />
            ))}
          </Stack>
          <EngagementSettingsDays days={days} onChange={setDays} />
          <Divider />
          <FieldProgress percentage={percentage} />
        </Stack>
        <Stack
          sx={{
            flexDirection: "row",
            gap: "4px",
            alignItems: "center",
          }}
        >
          <Stack
            onClick={() => setIsAllProducts(!isAllProducts)}
            component="span"
            sx={{
              flexDirection: "row",
              gap: "4px",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <CustomCheckbox checked={isAllProducts} />
            <Typography
              variant="body1"
              color="#1F2932"
              component="span"
              lineHeight="14px"
            >
              Set for all products
            </Typography>
          </Stack>
          {!isAllProducts && (
            <ReuseTooltip text="Current engagement score calculation settings will be applied to all your products.">
              <QuestionMarkInCircleIcon />
            </ReuseTooltip>
          )}
        </Stack>
      </Stack>
    </ReuseModal>
  );
};
